/**
 * useCreateVolume Hook
 *
 * Custom React Query mutation hook for creating volume resources.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { volumeService } from '../../../api/volume.service';
import type { VolumeCreateDTO } from '../../../types/volume.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateVolumeOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating volume
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useCreateVolume = (tenantId: string, options?: UseCreateVolumeOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VolumeCreateDTO) => volumeService.createVolume(tenantId, data),
    onSuccess: () => {
      // Invalidate all volume queries for this tenant (including different query params)
      queryClient.invalidateQueries({
        queryKey: ['volumes', tenantId],
        exact: false // This will match all queries that start with ['volumes', tenantId]
      });
      console.log('[useCreateVolume] Invalidated volume queries for tenant:', tenantId);
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
